import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Twitter,
  Youtube,
  Linkedin,
  Instagram,
  Facebook,
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

import {
  subscribeNewsletter,
  getSettings,
  getProducts,
} from "../api";

export default function Footer() {
  const [settings, setSettings] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getSettings().then(setSettings);

    getProducts().then((res) => {
      setProducts(res || []);
    });
  }, []);

  const onSignup = async (e) => {
    e.preventDefault();

    const email = e.target.email.value;

    if (!email) return;

    const res = await subscribeNewsletter(email);

    if (res && (res.status === "success" || res.ok)) {
      toast.success("Subscribed!", {
        description: `${email} has been added.`,
      });

      e.target.reset();
    } else {
      toast.error("Could not subscribe", {
        description:
          res?.message || "Please try again.",
      });
    }
  };

  const socialLinks = [
    {
      icon: Twitter,
      link:
        settings.social_twitter ||
        "https://twitter.com",
    },
    {
      icon: Youtube,
      link:
        settings.social_youtube ||
        "https://youtube.com",
    },
    {
      icon: Linkedin,
      link:
        settings.social_linkedin ||
        "https://linkedin.com",
    },
    {
      icon: Instagram,
      link:
        settings.social_instagram ||
        "https://instagram.com",
    },
    {
      icon: Facebook,
      link:
        settings.social_facebook ||
        "https://facebook.com",
    },
  ];

  return (
    <footer className="bg-white border-t border-[#ececef]">
      {/* ================= TOP CTA ================= */}

      <div className="border-b border-[#ececef] py-16 md:py-20">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 grid lg:grid-cols-[1.4fr,1fr] gap-12 items-center">
          <div>
            <p className="text-[#ef2435] text-[11px] tracking-[0.3em] uppercase font-semibold mb-5">
              [ BUILD WITH SSC ]
            </p>

            <h2 className="giant-text text-5xl md:text-7xl font-black leading-[0.9] text-gray-900">
              {settings.footer_cta_title_1 ||
                "START YOUR NEXT"}
              <br />

              <span className="text-[#ef2435]">
                {settings.footer_cta_title_2 ||
                  "PROJECT WITH SSC."}
              </span>
            </h2>
          </div>

          {/* Newsletter */}

          <form
            onSubmit={onSignup}
            className="space-y-4"
          >
            <p className="font-mono text-[11px] text-[#ef2435] tracking-widest font-semibold">
              [ NEWSLETTER ]
            </p>

            <p className="text-gray-600 text-sm leading-7">
              {settings.newsletter_description ||
                "Get updates on concrete innovations, infrastructure projects, and engineering insights."}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                name="email"
                type="email"
                required
                placeholder="Enter your email"
                className="h-12 rounded-full border-[#ececef] bg-[#f7f7f8]"
              />

              <Button
                type="submit"
                className="h-12 rounded-full px-6 bg-[#ef2435] hover:bg-[#d91f30]"
              >
                Subscribe
                <ArrowUpRight
                  size={14}
                  className="ml-2"
                />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* ================= PLANTS ================= */}

      <div className="border-b border-[#ececef] bg-[#fafafa] py-14">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8">
          <div className="flex items-center gap-3 mb-8">
            <MapPin
              size={18}
              className="text-[#ef2435]"
            />

            <p className="text-[#ef2435] text-[11px] tracking-[0.3em] uppercase font-semibold">
              [ OUR LOCATIONS ]
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                n: "01",
                title:
                  settings.plant_1_name ||
                  "Plant One",
                address: settings.plant_1,
              },
              {
                n: "02",
                title:
                  settings.plant_2_name ||
                  "Plant Two",
                address: settings.plant_2,
              },
              {
                n: "03",
                title:
                  settings.plant_3_name ||
                  "Plant Three",
                address: settings.plant_3,
              },
            ]
              .filter((x) => x.address)
              .map((p, i) => (
                <div
                  key={i}
                  className="bg-white border border-[#ececef] rounded-[28px] p-7"
                >
                  <span className="text-[#ef2435] text-xs font-semibold tracking-[0.25em]">
                    [ {p.n} ]
                  </span>

                  <h3 className="text-2xl font-black text-gray-900 mt-3 mb-3">
                    {p.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-7">
                    {p.address}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* ================= MAIN FOOTER ================= */}

      <div className="py-16">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* LOGO */}

          <div>
            <div className="flex items-center gap-4 mb-6">
              <img
                src={
                  settings.logo ||
                  "https://customer-assets.emergentagent.com/job_duramix-preview/artifacts/v8jsk3w3_ssc_logo.png"
                }
                alt="SSC"
                className="h-16 w-16 object-contain"
              />

              <div>
                <h3 className="text-2xl font-black text-gray-900">
                  {settings.company_name || "SSC"}
                </h3>

                <p className="text-[10px] tracking-[0.3em] text-gray-500 uppercase">
                  READY MIX CONCRETE
                </p>
              </div>
            </div>

            <p className="text-sm leading-7 text-gray-600">
              {settings.footer_about ||
                "Premium ready mix concrete manufacturer delivering durable, engineered concrete solutions for residential, commercial, and infrastructure projects."}
            </p>

            {/* SOCIALS */}

            <div className="flex gap-3 mt-6">
              {socialLinks.map((s, i) => {
                const Icon = s.icon;

                return (
                  <a
                    key={i}
                    href={s.link}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-full border border-[#ececef] flex items-center justify-center text-gray-500 hover:border-[#ef2435] hover:text-[#ef2435] transition-all"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* QUICK LINKS */}

          <div>
            <h4 className="text-sm tracking-[0.25em] uppercase text-[#ef2435] font-semibold mb-6">
              Quick Links
            </h4>

            <ul className="space-y-4 text-sm">
              {[
                ["/", "Home"],
                ["/why-sscreadymix", "About Us"],
                ["/products", "Products"],
                ["/works", "Projects"],
                ["/blogs", "Blogs"],
                ["/career", "Career"],
                ["/faq", "FAQ"],
                ["/contact", "Contact"],
              ].map(([link, title]) => (
                <li key={title}>
                  <Link
                    to={link}
                    className="text-gray-600 hover:text-[#ef2435] transition-colors"
                  >
                    {title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* PRODUCTS */}

          <div>
            <h4 className="text-sm tracking-[0.25em] uppercase text-[#ef2435] font-semibold mb-6">
              Our Products
            </h4>

            <ul className="space-y-4 text-sm">
              {products.length > 0 ? (
                products
                  .slice(0, 6)
                  .map((p, i) => (
                    <li key={p.id || i}>
                      <Link
                        to={`/products?product=${i}`}
                        className="text-gray-600 hover:text-[#ef2435] transition-colors"
                      >
                        {p.name}
                      </Link>
                    </li>
                  ))
              ) : (
                <>
                  <li className="text-gray-600">
                    M20 Concrete
                  </li>

                  <li className="text-gray-600">
                    M25 Concrete
                  </li>

                  <li className="text-gray-600">
                    Self Compacting Concrete
                  </li>

                  <li className="text-gray-600">
                    High Strength Concrete
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* CONTACT */}

          <div>
            <h4 className="text-sm tracking-[0.25em] uppercase text-[#ef2435] font-semibold mb-6">
              Contact Info
            </h4>

            <ul className="space-y-5 text-sm">
              <li className="flex gap-3">
                <Phone
                  size={16}
                  className="text-[#ef2435] mt-1"
                />

                <a
                  href={`tel:${
                    settings.phone ||
                    "+919861934840"
                  }`}
                  className="text-gray-600 hover:text-[#ef2435]"
                >
                  {settings.phone ||
                    "+91 9861 934 840"}
                </a>
              </li>

              <li className="flex gap-3">
                <Mail
                  size={16}
                  className="text-[#ef2435] mt-1"
                />

                <a
                  href={`mailto:${
                    settings.email ||
                    "info@sscreadymix.com"
                  }`}
                  className="text-gray-600 hover:text-[#ef2435] break-all"
                >
                  {settings.email ||
                    "info@sscreadymix.com"}
                </a>
              </li>

              <li className="flex gap-3">
                <MapPin
                  size={16}
                  className="text-[#ef2435] mt-1"
                />

                <div
  className="text-gray-700 leading-relaxed"
  dangerouslySetInnerHTML={{
    __html:
      settings.address_hq ||
      "SSC READYMIX Pvt. Ltd., Odisha, India",
  }}
/>
              </li>
            </ul>
          </div>

          {/* LEGAL */}

          <div>
            <h4 className="text-sm tracking-[0.25em] uppercase text-[#ef2435] font-semibold mb-6">
              Legal
            </h4>

            <ul className="space-y-4 text-sm">
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-gray-600 hover:text-[#ef2435]"
                >
                  Privacy Policy
                </Link>
              </li>

              <li>
                <Link
                  to="/terms-and-conditions"
                  className="text-gray-600 hover:text-[#ef2435]"
                >
                  Terms & Conditions
                </Link>
              </li>

              <li>
                <Link
                  to="/contact"
                  className="text-gray-600 hover:text-[#ef2435]"
                >
                  Request Quote
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ================= BOTTOM ================= */}

      <div className="border-t border-[#ececef] bg-[#fafafa] py-6">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-gray-500">
          <p>
            {settings.copyright_text ||
              "© 2026 SSC Ready Mix Concrete. All Rights Reserved."}
          </p>

          <p className="uppercase tracking-[0.2em]">
            {settings.footer_tagline ||
              "Built For Strength · Engineered For Performance"}
          </p>
        </div>
      </div>
    </footer>
  );
}